import { FC, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Select, IDropdownItem } from '@smartshell/ui-kit';

import {
  GET_AVAILABLE_DISCOUNTS,
  IDiscountsResponse,
} from 'components/DiscountsField/schema';
import type { TDiscount } from 'components/DiscountsField/types';
import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';

import s from './DiscountsField.module.scss';

export interface IDiscountFieldValue {
  id: number;
  value: number;
}

interface IDiscountFieldProps {
  value: IDiscountFieldValue | null;
  onChange: (value: IDiscountFieldValue | null) => void;
  disabled?: boolean;
}

const formatDiscountToOption = (
  discount: TDiscount
): IDropdownItem<number, TDiscount> => ({
  value: discount.id,
  title: discount.title,
  original: discount,
});

const renderOption = (item: IDropdownItem<number, TDiscount>): JSX.Element => (
  <div className={s.option}>
    <span>{item.title}</span>
    <span>{item.original?.value}%</span>
  </div>
);
const Field: FC<IDiscountFieldProps> = ({ value, onChange, disabled }) => {
  const { t } = useContext(LocalizationContext);
  const [options, setOptions] = useState<
    Array<IDropdownItem<number, TDiscount>>
  >([]);

  const { data } = useQuery<IDiscountsResponse>(GET_AVAILABLE_DISCOUNTS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data) {
        return;
      }

      setOptions(data?.getAvailableDiscounts?.map(formatDiscountToOption));
    },
  });

  useEffect(() => {
    if (disabled) onChange(null);
  }, [disabled]);

  return (
    <Select
      title={t('discount')}
      name="discounts-select"
      options={options}
      value={value?.id ?? null}
      errorWrapperClassName={s.wrapper}
      onChange={(newValue) => {
        if (!newValue || disabled) {
          return onChange(null);
        }

        const discount = data?.getAvailableDiscounts?.find(
          (el) => el.id === newValue
        );
        if (discount) {
          return onChange({
            id: discount.id,
            value: discount.value,
          });
        }
      }}
      disabled={disabled}
      renderOption={renderOption}
    />
  );
};

export const DiscountsField: FC<IDiscountFieldProps> = (props) => (
  <FeatureHOC alias="discounts">
    <Field {...props} />
  </FeatureHOC>
);
